import { formatMoney, TenantCurrency } from 'src/common/currency';
import { GenericEntity } from 'src/data-capture/types';
import { OwnerRowValue } from 'src/financial/financial-data/financial-item/forms/common/OwnersEditor';

type FoldBlocker = 'COPY_HAS_HISTORY' | 'COPY_OWNED_BY_ANOTHER_CLIENT';

/** An item a client already owns that is likely the same holding as the one being saved. */
export type LikelyCopy = {
  item_id: string;
  entity_type: string;
  description: string | null;
  provider: string | null;
  amount: number | null;
  match: 'SAME_REFERENCE' | 'CLOSE_VALUE';
  fold_blocker: FoldBlocker | null;
  /** Updated by a data feed or open banking. */
  is_connected: boolean;
};

export type CoOwnerCopy = LikelyCopy & { coOwnerId: string; coOwnerName: string };

/** A copy as it was before a detected item was saved onto it, in the fields the save changed. */
export type SavedOntoCopy = { item_id: string; previous_fields: GenericEntity };

// The clients a save adds as owners, other than the client whose page it is: only they can bring
// a copy of the item with them. Owners already stored were checked when they were added.
export function addedCoOwnerIds(
  owners: OwnerRowValue[] | undefined,
  storedOwners: OwnerRowValue[] | undefined,
  clientId: string,
): string[] {
  const stored = new Set((storedOwners ?? []).map(owner => owner.client_id));
  const added = (owners ?? [])
    .map(owner => owner.client_id)
    .filter(
      (ownerId): ownerId is string => !!ownerId && ownerId !== clientId && !stored.has(ownerId),
    );
  return [...new Set(added)];
}

// Names the copy the way an adviser would recognise it on the co-owner's Financial tab.
export function describeLikelyCopy(
  copy: Pick<LikelyCopy, 'description' | 'provider' | 'amount'>,
  currency: TenantCurrency,
): string {
  const name = copy.description || copy.provider || 'an item';
  const details = [
    copy.description ? copy.provider : null,
    copy.amount == null ? null : formatMoney(copy.amount, currency),
  ].filter(Boolean);
  return details.length > 0 ? `${name} (${details.join(', ')})` : name;
}
